import React, { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useEditorStore } from "../../state/editorStore";
import "./BatchEditor.css";

import BatchEditorHeader from "./BatchEditorHeader";
import BatchEditorLeftNav from "./BatchEditorLeftNav";
import BatchEditorLeftMenu from "./BatchEditorLeftMenu";

const BatchEditor = () => {

  const location = useLocation();
  const imagesFromState = location?.state?.images || [];

  const setImages = useEditorStore((state) => state.setImages);
  const resetObjectState = useEditorStore((state) => state.resetObjectState);
  const images = useEditorStore((state) => state.images || []);
  const layout = useEditorStore((state) => state.layout);
  const object = useEditorStore((state) => state.object);

  const [activeMenuIndex, setActiveMenuIndex] = useState(0);
  const [leftMenuOpen, setLeftMenuOpen] = useState(true);

  const contentAreaRefs = useRef([]);
  const [contentSizes, setContentSizes] = useState([]);

  /* ==============================
  LOAD IMAGES INTO STORE
  ============================== */

  useEffect(() => {
    if (imagesFromState?.length && images.length === 0) {
      resetObjectState();
      setImages(imagesFromState);
    }
  }, [imagesFromState, images.length, setImages, resetObjectState]);

  /* ==============================
  MEASURE CONTENT AREA
  ============================== */

  useEffect(() => {
    function updateSizes() {
      setContentSizes(
        contentAreaRefs.current.map((ref) =>
          ref
            ? { width: ref.offsetWidth, height: ref.offsetHeight }
            : { width: 1, height: 1 }
        )
      );
    }

    updateSizes();
    window.addEventListener("resize", updateSizes);

    return () => window.removeEventListener("resize", updateSizes);
  }, [images.length]);

  const handleMenuIndexChange = (idx) => {
    setActiveMenuIndex(idx);
    setLeftMenuOpen(true);
  };

  /* ==============================
  HELPERS
  ============================== */

  const hexToRgba = (hex, opacity) => {
    if (!hex) return "rgba(0,0,0,0)";
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  };

  const buildShadow = (shadow) => {

  if (!shadow?.enabled) return "";

  return `drop-shadow(
    ${shadow.x}px
    ${shadow.y}px
    ${shadow.blur}px
    ${hexToRgba(shadow.color, shadow.opacity)}
    )`;

  };

  const buildOutline = (outline) => {

  if (!outline?.enabled || outline.width === 0) return "";

  const w = outline.width;
  const blur = outline.blur || 0;
  const color = outline.color;

  return (
    `drop-shadow(${w}px 0 ${blur}px ${color}) ` +
    `drop-shadow(-${w}px 0 ${blur}px ${color}) ` +
    `drop-shadow(0 ${w}px ${blur}px ${color}) ` +
    `drop-shadow(0 -${w}px ${blur}px ${color})`
  );

};

  /* ==============================
  COMPONENT
  ============================== */

  return (
    <div className="batch-editor-page">

      <BatchEditorHeader />

      <div className="batch-editor-main">

        <BatchEditorLeftNav
          activeMenuIndex={activeMenuIndex}
          setActiveMenuIndex={handleMenuIndexChange}
        />

        <BatchEditorLeftMenu
          isOpen={leftMenuOpen}
          activeMenuIndex={activeMenuIndex}
        />

        <div className="batch-editor-workspace">

          <div className="batch-canvas-grid">

            {images.length === 0 ? (

              <div className="batch-canvas-placeholder">
                No images uploaded. Go back and upload images.
              </div>

            ) : (

              images.map((img, idx) => {

                const bbox = img.boundingBox;

                const paddingTop = layout.paddingTop || 0;
                const paddingBottom = layout.paddingBottom || 0;
                const paddingLeft = layout.paddingLeft || 0;
                const paddingRight = layout.paddingRight || 0;

                const contentW = contentSizes[idx]?.width || 1;
                const contentH = contentSizes[idx]?.height || 1;

                if (!bbox || bbox.width <= 0 || bbox.height <= 0) {

                  return (
                    <div className="batch-canvas-cell" key={img.id || idx}>

                      <div className="checkerboard-bg" />

                      <div className="frame">

                        <div
                          className="content-area"
                          ref={(el) => (contentAreaRefs.current[idx] = el)}
                          style={{
                            top: `${paddingTop}%`,
                            left: `${paddingLeft}%`,
                            left: `${paddingBottom}%`,
                            left: `${paddingRight}%`,
                            width: `${100 - paddingLeft - paddingRight}%`,
                            height: `${100 - paddingTop - paddingBottom}%`,
                          }}
                        >

                          <div className="align-layer">
                            <div className="object-wrapper">
                              <div className="object-transform-layer">

                                <img
                                  src={img.src || img.url}
                                  alt={img.name || `image-${idx}`}
                                  className="batch-canvas-img"
                                  loading="lazy"
                                />

                              </div>
                            </div>
                          </div>

                        </div>

                      </div>

                    </div>
                  );
                }

                const scale = Math.min(
                  contentW / bbox.width,
                  contentH / bbox.height
                );

                let alignX = 0;
                let alignY = 0;

                if (layout.alignX === "left") alignX = -1;
                else if (layout.alignX === "right") alignX = 1;

                if (layout.alignY === "top") alignY = -1;
                else if (layout.alignY === "bottom") alignY = 1;

                const offsetX =
                  (contentW - bbox.width * scale) / 2 -
                  bbox.left * scale +
                  (alignX * (contentW - bbox.width * scale)) / 2;

                const offsetY =
                  (contentH - bbox.height * scale) / 2 -
                  bbox.top * scale +
                  (alignY * (contentH - bbox.height * scale)) / 2;

                const userTransform = `
                  rotate(${object.rotation}deg)
                  scaleX(${object.flipH ? -1 : 1})
                  scaleY(${object.flipV ? -1 : 1})
                `;

                const bboxTransform = `translate(${offsetX}px, ${offsetY}px) scale(${scale})`;

                return (
                  <div className="batch-canvas-cell" key={img.id || idx}>

                    <div className="checkerboard-bg" />

                    <div className="frame">

                      <div
                        className="content-area"
                        ref={(el) => (contentAreaRefs.current[idx] = el)}
                        style={{
                          top: `${paddingTop}%`,
                          left: `${paddingLeft}%`,
                          width: `${100 - paddingLeft - paddingRight}%`,
                          height: `${100 - paddingTop - paddingBottom}%`,
                        }}
                      >

                        <div className="align-layer">
                          <div className="object-wrapper">

                            <div
                              className="object-transform-layer"
                              style={{
                                transform: `
                                rotate(${object.rotation}deg)
                                scaleX(${object.flipH ? -1 : 1})
                                scaleY(${object.flipV ? -1 : 1})
                                `,
                              }}
                            >

                              <img
                                src={img.src || img.url}
                                alt={img.name || `image-${idx}`}
                                className="batch-canvas-img"
                                loading="lazy"
                                style={{
                                  
                                  filter: `
                                    ${buildShadow(object.shadow)}
                                    ${buildOutline(object.outline)}
                                  `,
                                }}
                              />

                            </div>

                          </div>
                        </div>

                      </div>

                    </div>

                  </div>
                );
              })

            )}

          </div>

        </div>

      </div>

    </div>
  );
};

export default BatchEditor;